import type React from 'react';
import * as yup from 'yup';

import type { DynamicComponent } from '../dynamicComponent';
import { type CellComponent, type DefinitionComponent, strategiesType } from '../types';

import { ListFieldWrapper } from './listFieldWrapper';
import type { ChangeComponentProps } from './simpleFieldWrappers';

interface TaxReturnRuntime {
  readOnly: boolean;
}

interface CatalogItem {
  id?: string | number;
  name?: string;
}

interface UseCatalogListResult<TCatalogItem extends CatalogItem = CatalogItem> {
  data?: Array<TCatalogItem>;
  loading?: boolean;
  error?: unknown;
}

type UseCatalogList<TCatalogItem extends CatalogItem = CatalogItem> = (
  catalogName: string
) => [UseCatalogListResult<TCatalogItem>, ...Array<unknown>];

export interface ListStrategyFactoryOptions {
  ChangeComponent?: React.ComponentType<ChangeComponentProps>;
  requiredValidation: yup.Schema<unknown>;
  useCatalogList: UseCatalogList;
  useRuntime: () => TaxReturnRuntime;
}

export const createListStrategy = ({
  ChangeComponent,
  requiredValidation,
  useCatalogList,
  useRuntime
}: ListStrategyFactoryOptions): DynamicComponent => ({
  Component: (definition: DefinitionComponent) => {
    const { readOnly } = useRuntime();
    const def = definition as CellComponent;

    return (
      <ListFieldWrapper
        nameCatalog={def.catalogName}
        readOnly={!def.editable || readOnly}
        isFormula={def.isFormula}
        label={def.name}
        name={def.fieldPath ?? def.code}
        description={def.description}
        ChangeComponent={ChangeComponent}
        useCatalogList={useCatalogList}
      />
    );
  },
  CanGet: (type: string) => type === strategiesType.list,
  Validations: (definition: DefinitionComponent) => {
    const def = definition as CellComponent;
    const validation: Record<string, yup.Schema<unknown>> = {};

    if (def.mandatory) {
      validation[definition.code] = requiredValidation;
    }

    return yup.object().shape(validation);
  }
});
